import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Button, Modal, Segmented, Switch } from '../../../components/ui';
import { useGuardianMutations } from '../../../api/hooks';
import type { Guardian, GuardianInput, GuardianRelationship } from '../../../api/types';

const LABEL = 'mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const FIELD = 'min-h-[44px] w-full rounded-[12px] border border-line-strong bg-surface px-3.5 py-2 text-sm text-ink outline-none transition placeholder:text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-slate';

export const RELATIONSHIP_LABEL: Record<GuardianRelationship, string> = {
  mother: 'Mother', father: 'Father', guardian: 'Guardian', other: 'Other',
};

/** The first message in a DRF 400 body ({field: msg | [msg]} or {detail}). */
function apiMessage(e: unknown): string | null {
  if (!isAxiosError(e) || !e.response?.data || typeof e.response.data !== 'object') return null;
  const first = Object.values(e.response.data as Record<string, unknown>)[0];
  const msg = Array.isArray(first) ? first[0] : first;
  return typeof msg === 'string' ? msg : null;
}

/** Student 360 → Parent contact → add or edit one guardian. */
export default function GuardianDialog({ isOpen, onClose, studentId, studentName, guardian }: {
  isOpen: boolean; onClose: () => void; studentId: number; studentName?: string;
  /** Edit this guardian; absent = add a new one. */
  guardian?: Guardian | null;
}) {
  const { create, update } = useGuardianMutations(studentId);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState<GuardianRelationship>('guardian');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [receivesDigest, setReceivesDigest] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const editing = !!guardian;
  const studentAdded = guardian?.source === 'student';

  useEffect(() => {
    if (!isOpen) return;
    setName(guardian?.name ?? '');
    setRelationship(guardian?.relationship ?? 'guardian');
    setEmail(guardian?.email ?? '');
    setPhone(guardian?.phone ?? '');
    setReceivesDigest(guardian?.receives_digest ?? true);
    setError(null);
  }, [isOpen, guardian]);

  const empty = !name.trim() && !email.trim() && !phone.trim();
  const busy = create.isPending || update.isPending;

  async function save() {
    if (empty) return;
    setError(null);
    const body: GuardianInput = { name: name.trim(), relationship, email: email.trim(), phone: phone.trim() };
    if (!studentAdded) body.receives_digest = receivesDigest;
    try {
      if (guardian) await update.mutateAsync({ id: guardian.id, body });
      else await create.mutateAsync(body);
      onClose();
    } catch (e) {
      setError(apiMessage(e) ?? 'Could not save the guardian. Try again.');
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" eyebrow="Parent contact"
      title={editing ? `Edit ${guardian?.name || 'guardian'}` : `Add a guardian${studentName ? ` for ${studentName}` : ''}`}
      subtitle="Guardians get the weekly digest by email or Telegram."
      initialFocus={() => document.getElementById('guardian-name')}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={empty}>{editing ? 'Save changes' : 'Add guardian'}</Button>
      </>}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
        {error && <p role="alert" className="text-sm text-danger-ink">{error}</p>}
        <div>
          <label htmlFor="guardian-name" className={LABEL}>Name</label>
          <input id="guardian-name" value={name} maxLength={128} autoComplete="off"
            onChange={(e) => setName(e.target.value)} placeholder="Full name" className={FIELD} />
        </div>
        <div>
          <span className={LABEL}>Relationship</span>
          <Segmented<GuardianRelationship> dense label="Relationship" value={relationship} onChange={setRelationship}
            options={(Object.keys(RELATIONSHIP_LABEL) as GuardianRelationship[]).map((k) => ({ key: k, label: RELATIONSHIP_LABEL[k] }))} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="guardian-email" className={LABEL}>Email</label>
            <input id="guardian-email" type="email" value={email} autoComplete="off"
              onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className={FIELD} />
          </div>
          <div>
            <label htmlFor="guardian-phone" className={LABEL}>Phone</label>
            <input id="guardian-phone" type="tel" value={phone} maxLength={32} autoComplete="off"
              onChange={(e) => setPhone(e.target.value)} placeholder="09…" className={FIELD} />
          </div>
        </div>
        {studentAdded ? (
          <p className="rounded-[12px] bg-note px-3.5 py-2.5 text-sm text-ink">
            The student added this guardian, so the weekly digest follows the student's consent.
          </p>
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-[12px] border border-line px-3.5 py-2.5">
            <div>
              <p id="guardian-digest-label" className="text-sm font-semibold text-ink">Send the weekly digest</p>
              <p className="text-xs text-muted">By email, and by Telegram once they open their link.</p>
            </div>
            <Switch checked={receivesDigest} onChange={setReceivesDigest} label="Send the weekly digest"
              labelledBy="guardian-digest-label" />
          </div>
        )}
        {empty && <p className="text-xs text-muted">Add a name, an email or a phone number.</p>}
      </form>
    </Modal>
  );
}
